// @ts-nocheck
import React from 'react';

import { Handle } from 'reactflow';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Card, Popper, Fade } from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as CodeOffIcon } from '../../assets/images/codeIcon.svg';
import 'reactflow/dist/style.css';

const DetailsToShowOnHover = ({ data }) => {
  const inputData =
    data?.node_input &&
    Object?.keys(data?.node_input)?.length &&
    data?.node_input?.filter(
      (n) => n?.type === 'dataset' || n?.type === 'model'
    );
  const outputData =
    data?.node_output &&
    Object?.keys(data?.node_output)?.length &&
    data?.node_output?.filter(
      (n) => n?.type === 'dataset' || n?.type === 'model'
    );
  return (
    <Card
      variant="outlined"
      sx={(_theme) => ({
        borderColor: () => _theme?.palette.other.blue3,
        minWidth: '80px',
        maxWidth: '600px',
        padding: 1,
      })}
    >
      <Box>
        {data?.defective ? (
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%', // Set the max width for truncation
            }}
          >
            This node has become defective. You can delete this node and add it
            again.
          </Typography>
        ) : (
          <Box
            display="flex"
            width="100%"
            flexDirection={
              inputData?.length + outputData?.length > 1 ? 'row' : 'column'
            }
          >
            <Box display="flex">
              <Typography variant="body3">UDF:</Typography>
              <Typography
                variant="body4"
                ml={1}
                mr={inputData?.length + outputData?.length > 1 ? 2 : 0}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '130px', // Adjust this width as needed
                }}
              >
                {data?.udfDetails?.name}
              </Typography>
            </Box>
            <Box display="flex">
              <Typography variant="body3" mr={1}>
                Node name:
              </Typography>
              <Typography
                variant="body4"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '130px', // Adjust this width as needed
                }}
              >
                {data?.label}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      {!data?.defective &&
        (inputData?.length > 0 || outputData?.length > 0) && (
          <Box mt={2} width="100%">
            {inputData?.length > 0 && (
              <Box width="100%" display="flex" flexDirection="column">
                <Typography variant="body3">Node input</Typography>
                <Box
                  width="100%"
                  display="flex"
                  flexWrap="wrap" // Enable wrapping
                >
                  {inputData?.map((item, idx) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`input${idx}`}
                    >
                      <Typography variant="body4" mr={1}>
                        {item?.label}
                      </Typography>
                      <Typography variant="body4" mr={1}>
                        ({item?.type})
                      </Typography>
                      <Typography variant="body4" mr={1}>
                        {idx !== inputData?.length - 1 ? '|' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {outputData?.length > 0 && (
              <Box width="100%" mt={2}>
                <Typography variant="body3">Node output</Typography>
                <Box
                  width="100%"
                  display="flex"
                  flexWrap="wrap" // Enable wrapping
                  sx={{ wordWrap: 'break-word' }}
                >
                  {outputData?.map((item, idx) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`output${idx}`}
                    >
                      <Typography variant="body4" mr={1}>
                        {item?.label}
                      </Typography>
                      <Typography variant="body4" mr={1}>
                        ({item?.type})
                      </Typography>
                      <Typography variant="body4" mr={1}>
                        {idx !== outputData?.length - 1 ? '|' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
    </Card>
  );
};

DetailsToShowOnHover.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

// PiplineNode
const PipelineNode = ({ data }) => {
  const theme = useTheme();
  const [isHovering, setIsHovering] = React.useState(false);
  const anchorEl = React.useRef(null);

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };
  return (
    <Box sx={{ cursor: 'pointer' }}>
      <Box
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        ref={anchorEl}
      >
        <Card
          elevation={1}
          sx={{
            borderWidth: data.isChild ? 0 : '1px',
            borderStyle: data?.defective ? 'dashed' : 'solid',
            borderColor: (_theme) =>
              data?.defective
                ? _theme.palette.error?.main
                : _theme.palette.primary.light,
            display: 'flex',
            flexGrow: 1,
            borderRadius: '8px',
            maxWidth: '200px',
            minWidth: '80px',
            maxHeight: '28px',
            minHeight: '28px',
            paddingX: 0.5,
          }}
        >
          <Box
            pr={0.4}
            pl={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              animation: data?.defective
                ? 'blinker 1s linear infinite'
                : 'none',
            }}
          >
            <CodeOffIcon color="primary" width={14} height={14} />
          </Box>
          <Box
            px={0.2}
            py={0.2}
            m={0.5}
            width="100%"
            overflow="hidden"
            textOverflow="ellipsis"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              variant="body7"
            >
              {data?.label}
            </Typography>
          </Box>
        </Card>
      </Box>
      <Handle
        type="source"
        position="right"
        id="b"
        style={{
          background: theme.palette.primary.main,
          height: '4px',
          width: '4px',
        }}
        isConnectable={true}
      />
      <Handle
        type="target"
        position="left"
        id="b"
        style={{
          background: theme.palette.primary.main,
          height: '4px',
          width: '4px',
        }}
        isConnectable={true}
      />
      <Popper
        open={isHovering}
        anchorEl={anchorEl?.current}
        transition
        placement="bottom-start"
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Box mt={0.5}>
              <DetailsToShowOnHover data={data} />
            </Box>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

PipelineNode.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
export default PipelineNode;
